import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Navigation, ArrowRight, Compass, Utensils, Smile, TrendingUp, TrendingDown, Wallet, Bell, Star, Flame } from "lucide-react";
import { api } from "../lib/api";
import { TruckCard } from "../components/TruckCard";
import { CUISINES } from "../lib/cuisines";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";
import { toast } from "sonner";

const FILTERS = [
  { key: "tout", label: "Tout" },
  { key: "Burger", label: "Burger" },
  { key: "Tacos", label: "Tacos" },
  { key: "Vegan", label: "Vegan" },
  { key: "Pizza", label: "Pizza" },
  { key: "open", label: "Ouvert maintenant" },
];

export default function Home() {
  const nav = useNavigate();
  const [ville, setVille] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [filter, setFilter] = useState("tout");
  const [trucks, setTrucks] = useState([]);
  const [launch, setLaunch] = useState({ remaining: 100, limit: 100 });

  const load = async (f = filter) => {
    const params = {};
    if (f === "open") params.open_now = true;
    else if (f !== "tout") params.cuisine = f;
    const { data } = await api.get("/trucks", { params });
    setTrucks(data.results);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load("tout"); }, []);
  useEffect(() => { api.get("/launch-status").then((r) => setLaunch(r.data)).catch(() => {}); }, []);

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (ville) p.set("city", ville);
    if (cuisine) p.set("cuisine", cuisine);
    nav(`/recherche?${p.toString()}`);
  };

  const geo = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation non disponible");
    navigator.geolocation.getCurrentPosition(
      (pos) => nav(`/recherche?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}&sort=proximity`),
      () => toast.error("Impossible d'obtenir votre position")
    );
  };

  const homeJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "StreatEat",
    url: (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, ""),
    description: "L'annuaire n°1 des foodtrucks en France.",
    potentialAction: {
      "@type": "SearchAction",
      target: `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/recherche?city={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <div>
      <Seo path="/" jsonLd={homeJsonLd} />
      {(launch.remaining ?? 0) > 0 && (
        <button
          onClick={() => nav("/tarifs#simulateur")}
          className="w-full bg-gradient-to-r from-street-gold to-coral-twist text-white text-sm sm:text-base font-bold py-2.5 px-4 flex items-center justify-center gap-2 hover:brightness-105 transition-all active:scale-[0.99]"
          data-testid="launch-banner"
        >
          <Flame size={16} className="fill-white/20 shrink-0" />
          <span>Offre de lancement : plus que <span className="tabular-nums">{launch.remaining}/{launch.limit || 100}</span> places à -50% à vie</span>
          <span className="hidden sm:inline-flex items-center gap-1 underline underline-offset-2">En profiter <ArrowRight size={14} /></span>
        </button>
      )}
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-full text-sm font-bold text-urban-teal shadow-warm">
              <MapPin size={15} className="text-coral-twist" /> L'annuaire n°1 des foodtrucks
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold text-urban-teal mt-5 leading-tight">
              Le foodtruck que vous cherchez est déjà <span className="text-street-gold">en route.</span>
            </h1>
            <p className="text-base sm:text-lg text-urban-teal/70 mt-5 max-w-lg">
              StreatEat recense les foodtrucks et leurs emplacements du jour, en temps réel. Une ville, une envie : trouvez qui cuisine près de vous, maintenant.
            </p>
            <div className="flex flex-wrap gap-4 mt-6 text-sm font-semibold text-urban-teal/70">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-fresh-mint" /> Gratuit pour se référencer</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-fresh-mint" /> Sans carte bancaire</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-fresh-mint" /> Emplacements du jour</span>
            </div>
          </div>

          {/* Search card */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            className="bg-white rounded-3xl shadow-warm-lg p-6 sm:p-8">
            <h2 className="font-display font-bold text-xl text-urban-teal mb-4">Trouver un foodtruck</h2>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-urban-teal/50">Où ?</label>
                  <div className="mt-1">
                    <AddressAutocomplete value={ville} onChange={setVille} onSelect={(s) => setVille(s.city || s.label)} placeholder="Ville..." testid="hero-ville-input" Icon={MapPin} cityOnly />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-urban-teal/50">Cuisine</label>
                  <div className="flex items-center gap-2 border-2 border-cream-bg focus-within:border-urban-teal rounded-2xl px-3 py-2.5 mt-1 transition-colors">
                    <Utensils size={18} className="text-street-gold" />
                    <input value={cuisine} onChange={(e) => setCuisine(e.target.value)} placeholder="Tacos, Burger, Vegan..." className="w-full outline-none bg-transparent text-urban-teal" data-testid="hero-cuisine-input" />
                  </div>
                </div>
              </div>
              <Button type="submit" className="w-full rounded-2xl h-12 bg-street-gold hover:bg-street-gold/90 text-white font-bold text-base gap-2" data-testid="hero-search-btn">
                <Search size={18} /> Rechercher
              </Button>
              <button type="button" onClick={geo} className="w-full flex items-center justify-center gap-2 text-urban-teal font-semibold text-sm hover:text-street-gold transition-colors" data-testid="hero-geo-btn">
                <Navigation size={15} /> Autour de moi
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* NOS FOODTRUCKS */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-11 h-11 rounded-full bg-urban-teal flex items-center justify-center"><MapPin size={20} className="text-white" /></span>
            <div>
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-urban-teal">Nos foodtrucks</h2>
              <p className="text-urban-teal/60 text-sm">Parcourez l'annuaire et filtrez par cuisine ou disponibilité.</p>
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => { setFilter(f.key); load(f.key); }}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-colors ${filter === f.key ? "bg-street-gold text-white" : "bg-cream-bg text-urban-teal hover:bg-cream-bg/60"}`}
                data-testid={`filter-${f.key}`}>{f.label}</button>
            ))}
          </div>

          {trucks.length === 0 ? (
            <p className="text-center text-urban-teal/50 py-10" data-testid="home-empty">Aucun foodtruck dans cette catégorie pour l'instant.</p>
          ) : (
            <div className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-4">
              {trucks.map((t, i) => (
                <div key={t.slug} className="snap-start shrink-0 w-[280px]"><TruckCard truck={t} index={i} /></div>
              ))}
            </div>
          )}

          <div className="text-center mt-8">
            <Button onClick={() => nav(`/recherche${filter !== "tout" ? (filter === "open" ? "?open_now=true" : `?cuisine=${filter}`) : ""}`)}
              className="rounded-full bg-urban-teal hover:bg-urban-teal/90 text-white font-bold px-8 h-12 gap-2" data-testid="discover-all-btn">
              Découvrir tous nos foodtrucks <ArrowRight size={17} />
            </Button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-urban-teal">Trouver à manger n'a jamais été aussi direct</h2>
          <p className="text-urban-teal/60 mt-3">Trois étapes, zéro friction - pensé pour une recherche en marchant.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Localisez", d: "Activez votre position ou tapez une ville. On affiche tout de suite les foodtrucks autour de vous.", Icon: Compass, c: "#FF9F1C" },
            { n: "02", t: "Découvrez", d: "Spécialités, horaires du jour et emplacement exact : toute l'info utile, mise à jour par le gérant.", Icon: Utensils, c: "#2EC4B6" },
            { n: "03", t: "Régalez-vous", d: "Un tap pour lancer l'itinéraire. Vous savez déjà qu'il sera ouvert quand vous arriverez.", Icon: Smile, c: "#FF6B6B" },
          ].map((s) => (
            <div key={s.n} className="bg-white rounded-2xl p-7 shadow-warm relative">
              <span className="font-display font-extrabold text-5xl text-cream-bg absolute top-4 right-5">{s.n}</span>
              <span className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${s.c}22` }}><s.Icon size={26} style={{ color: s.c }} /></span>
              <h3 className="font-display font-bold text-xl text-urban-teal">{s.t}</h3>
              <p className="text-urban-teal/60 text-sm mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-4">
        <div className="bg-urban-teal rounded-3xl p-10 sm:p-14 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-extrabold text-white">Vous gérez un foodtruck ?</h2>
          <p className="text-white/70 mt-3 max-w-xl mx-auto">Référencez votre activité gratuitement, à vie. Déclarez votre emplacement du jour en 30 secondes et soyez trouvé aujourd'hui.</p>
          <div className="flex flex-wrap gap-3 justify-center mt-6">
            <Button onClick={() => nav("/connexion?tab=signup")} className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold px-8 h-12" data-testid="cta-register">Référencer mon foodtruck</Button>
            <Button onClick={() => nav("/tarifs")} variant="outline" className="rounded-full border-white/40 text-white bg-transparent hover:bg-white/10 font-bold px-8 h-12" data-testid="cta-pricing">Voir les offres</Button>
          </div>
        </div>
      </section>

      {/* MANAGER MARKETING - Avant / Après */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="manager-marketing">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="inline-block bg-street-gold/15 text-street-gold px-3 py-1 rounded-full text-sm font-bold">Spécial gérants de foodtruck</span>
          <h2 className="text-2xl sm:text-4xl font-display font-extrabold text-urban-teal mt-4">Arrêtez de rouler à vide. Remplissez vos jours creux.</h2>
          <p className="text-urban-teal/60 mt-3">Le vrai problème n'est pas votre cuisine - c'est que vos clients ne savent pas où vous êtes aujourd'hui. StreatEat vous rend visible au bon endroit, au bon moment. Et ça se voit sur le tiroir-caisse.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl p-7 border-2 border-coral-twist/30 bg-coral-twist/5" data-testid="marketing-before">
            <div className="flex items-center gap-2 text-coral-twist font-display font-bold text-lg"><TrendingDown size={22} /> Avant StreatEat</div>
            <ul className="mt-4 space-y-2.5 text-sm text-urban-teal/70">
              <li className="flex gap-2"><span className="text-coral-twist font-bold">✕</span> Personne ne sait où vous êtes aujourd'hui</li>
              <li className="flex gap-2"><span className="text-coral-twist font-bold">✕</span> Toujours les mêmes habitués, bouche-à-oreille limité</li>
              <li className="flex gap-2"><span className="text-coral-twist font-bold">✕</span> Des jours creux à moitié vides</li>
              <li className="flex gap-2"><span className="text-coral-twist font-bold">✕</span> Aucun moyen de prévenir vos clients quand vous changez d'emplacement</li>
            </ul>
            <div className="bg-white rounded-2xl p-4 mt-5">
              <p className="text-[11px] uppercase font-bold text-urban-teal/40">Résultat type</p>
              <p className="font-display font-extrabold text-2xl text-coral-twist mt-0.5">~120 couverts / semaine</p>
              <p className="text-sm text-urban-teal/50">Un potentiel de chiffre d'affaires laissé sur la route.</p>
            </div>
          </div>

          <div className="rounded-3xl p-7 border-2 border-fresh-mint/40 bg-fresh-mint/5" data-testid="marketing-after">
            <div className="flex items-center gap-2 text-fresh-mint font-display font-bold text-lg"><TrendingUp size={22} /> Avec StreatEat</div>
            <ul className="mt-4 space-y-2.5 text-sm text-urban-teal/80">
              <li className="flex gap-2"><span className="text-fresh-mint font-bold">✓</span> Visible sur l'annuaire n°1 des foodtrucks, dans votre zone</li>
              <li className="flex gap-2"><span className="text-fresh-mint font-bold">✓</span> Emplacement du jour en temps réel + itinéraire en 1 tap</li>
              <li className="flex gap-2"><span className="text-fresh-mint font-bold">✓</span> Alertes automatiques à vos abonnés à chaque déplacement</li>
              <li className="flex gap-2"><span className="text-fresh-mint font-bold">✓</span> Avis clients & fidélité qui transforment un passage en habitude</li>
            </ul>
            <div className="bg-white rounded-2xl p-4 mt-5">
              <p className="text-[11px] uppercase font-bold text-urban-teal/40">Résultat type</p>
              <p className="font-display font-extrabold text-2xl text-fresh-mint mt-0.5">~150 couverts / semaine <span className="text-base">(+25%)</span></p>
              <p className="text-sm font-bold text-fresh-mint">≈ +1 500 € de chiffre d'affaires / mois*</p>
            </div>
          </div>
        </div>

        <div className="bg-urban-teal rounded-3xl p-8 mt-6 grid sm:grid-cols-3 gap-6 text-center">
          <div className="text-white"><Wallet size={26} className="text-street-gold mx-auto" /><p className="font-display font-extrabold text-xl mt-2">+1 500 €/mois</p><p className="text-white/60 text-sm">de CA supplémentaire estimé pour ~6 €/mois d'abonnement</p></div>
          <div className="text-white"><Bell size={26} className="text-fresh-mint mx-auto" /><p className="font-display font-extrabold text-xl mt-2">1 clic = tous prévenus</p><p className="text-white/60 text-sm">vos abonnés savent où vous trouver, même quand vous bougez</p></div>
          <div className="text-white"><Star size={26} className="text-street-gold mx-auto fill-street-gold" /><p className="font-display font-extrabold text-xl mt-2">Plus d'avis, plus de confiance</p><p className="text-white/60 text-sm">la preuve sociale qui fait choisir votre camion plutôt qu'un autre</p></div>
        </div>
        <p className="text-xs text-urban-teal/40 text-center mt-3">*Estimation pour un panier moyen de 12 € et +25 % de nouveaux clients. Simulez votre propre gain avec le calculateur sur la page Tarifs.</p>

        <div className="flex flex-wrap gap-3 justify-center mt-7">
          <Button onClick={() => nav("/connexion?tab=signup")} className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold px-8 h-12" data-testid="marketing-register-btn">Référencer mon foodtruck gratuitement</Button>
          <Button onClick={() => nav("/tarifs#simulateur")} variant="outline" className="rounded-full border-urban-teal/20 text-urban-teal font-bold px-8 h-12" data-testid="marketing-roi-btn">Calculer mon gain</Button>
        </div>
      </section>
    </div>
  );
}
